import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { SCROLL_DEPTHS } from "@/lib/analytics/events";
import { trackPageView, trackScrollDepth, trackSessionEnded } from "@/lib/analytics";

export function usePageAnalytics() {
  const location = useLocation();
  const reachedDepthsRef = useRef(new Set());
  const sessionEndedRef = useRef(false);

  useEffect(() => {
    trackPageView(location.pathname);
    reachedDepthsRef.current = new Set();
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;

      const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);

      for (const depth of SCROLL_DEPTHS) {
        const key = `${location.pathname}:${depth}`;
        if (scrollPercent >= depth && !reachedDepthsRef.current.has(key)) {
          reachedDepthsRef.current.add(key);
          trackScrollDepth(depth, location.pathname);
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  useEffect(() => {
    const endSessionOnce = () => {
      if (sessionEndedRef.current) return;
      sessionEndedRef.current = true;
      trackSessionEnded();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        endSessionOnce();
      }
    };

    window.addEventListener("pagehide", endSessionOnce);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", endSessionOnce);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);
}
